import React from 'react';
import { Button, Spin, Dropdown } from 'antd';
import s from './s.module.less';
import dayjs from 'utils/dayjs';
import { getTimeStr } from 'utils/calendar';
import StarReview from 'components/StarReview';
import { EReviewSource, TReviewComment, TReviewItem } from 'pages/dashboard/PPAKlarityReviewsDashboradPage/types';
import AjustText from 'components/AjustText';
import { getKlarityReviewReply } from 'api/survey';
import { getPracticeReviewReply } from 'api/group';
import ReviewDisputeStore from 'store/ReviewDispute';
import { EDISPUTE_STATUS } from 'types/common';
import DisputeStatus from 'components/DisputeStatusTag';
import OriginDayjs from 'dayjs';
import ClockIcon from 'pages/dashboard/ProviderHomePage/components/TodayAppointments/icons/ClockIcon';

type Props = {
    review?: TReviewItem;
    onClickReply?: () => void;
    isShow?: boolean;
    isPracticeView?: boolean;
    hideRightPart?: boolean;
    hideBorder?: boolean;
    useBlueActions?: boolean;
};

const ReviewItem = (props: Props) => {
    const { review, onClickReply, isShow, isPracticeView, hideRightPart, hideBorder, useBlueActions } = props;
    const [, setDisputeStore] = ReviewDisputeStore.useStore();
    const [showReply, setShowReply] = React.useState(false);
    const [replyComments, setReplyComments] = React.useState<TReviewComment[]>();
    const [loadingReply, setLoadingReply] = React.useState(false);
    const showDispute = !!(review && review?.rating <= 3);

    // 判断是否超过30天回复期限
    const isReplyPeriodExpired = () => {
        if (!review?.submitAt) return false;
        const submitDate = dayjs(review.submitAt);
        const now = dayjs();
        const daysDiff = now.diff(submitDate, 'day');
        return daysDiff > 30;
    };

    const replyPeriodExpired = isReplyPeriodExpired();
    const canReply = !replyPeriodExpired;
    const hasReply = !!review?.isReplied;

    // 计算剩余回复时间（30天内倒数）
    const getRemainingTimeInfo = () => {
        // 只有在未过期且未回复时才显示
        if (!review?.submitAt || replyPeriodExpired || hasReply) {
            return null;
        }

        const submitDate = dayjs(review.submitAt);
        const now = dayjs();
        const deadline = submitDate.add(30, 'day');
        const remainingMs = deadline.diff(now);

        if (remainingMs <= 0) return null;

        const remainingHours = Math.floor(remainingMs / (1000 * 60 * 60));
        const remainingDays = Math.floor(remainingHours / 24);

        if (remainingDays > 3) {
            return {
                className: s.moreThan3Days,
                text: `${remainingDays} days left`,
            };
        } else if (remainingDays >= 1) {
            return {
                className: s.oneToThreeDays,
                text: `${remainingDays} ${remainingDays === 1 ? 'day' : 'days'} left`,
            };
        } else if (remainingHours >= 4) {
            return {
                className: s.fourTo24Hours,
                text: `${remainingHours} ${remainingHours === 1 ? 'hour' : 'hours'} left`,
            };
        } else {
            return {
                className: s.lessThan4Hours,
                text: `${remainingHours} ${remainingHours === 1 ? 'hour' : 'hours'} left`,
            };
        }
    };

    const fetchReply = React.useCallback(async () => {
        if (review?.comment?.id) {
            setLoadingReply(true);
            const result =
                isPracticeView && review?.bpProviderId
                    ? await getPracticeReviewReply(review.bpProviderId, review.id)
                    : await getKlarityReviewReply(review?.id);
            if (!result?.error) {
                const replys: TReviewComment[] = result?.data?.data;
                setReplyComments(replys);
            }
            setLoadingReply(false);
        }
    }, [review?.comment?.id, review?.id, isPracticeView, review?.bpProviderId]);

    const handleShowDisputeDetail = () => {
        setDisputeStore('currentReview', review);
        setDisputeStore('isPracticeView', !!isPracticeView);
        setDisputeStore('showViewModal', true);
    };

    const handleMenuClick = (e: { key: string }) => {
        const { key } = e;

        if (key === '1') {
            setDisputeStore('currentReview', review);
            setDisputeStore('isPracticeView', !!isPracticeView);
            setDisputeStore('showEditModal', true);
            setDisputeStore('showViewModal', false);
        }
    };

    React.useEffect(() => {
        if (hasReply) {
            fetchReply();
        }
    }, [hasReply, fetchReply]);

    if (!review) {
        return null;
    }

    const remainingTimeInfo = getRemainingTimeInfo();
    const verified = review.source !== EReviewSource.PROVIDER_IMPORT;

    // 目前列表视图不展示 email/phone，如果后续需要可从 reviewerEmail / reviewerPhone 读取

    const displayComment: TReviewComment | undefined = replyComments ? replyComments[0] : undefined;

    const hasTag = !!review?.tags?.trim();
    const showRate = verified || (review?.rating || 0) > 0;
    //const showRate = false

    const menuItems = [
        {
            key: '1',
            label: 'Dispute',
        },
    ];

    const buttonRender = () => {
        // 仅负责渲染 Reply / Replied 按钮本身，不包含三点菜单
        // 1. 已回复时始终显示 "Replied"（灰色禁用）
        if (hasReply) {
            return (
                <Button onClick={onClickReply} className={s.btnDisabled} disabled>
                    Replied
                </Button>
            );
        }

        // 2. 没有回复时，根据是否在30天有效期内渲染 Reply（过期则禁用）
        return (
            <Button onClick={onClickReply} className={canReply ? s.btn : s.btnDisabled} disabled={!canReply}>
                Reply
            </Button>
        );
    };

    return (
        <div className={`${s.wrap} ${hideBorder ? s.hideBorder : ''}`} key={`${review.id}`}>
            <div className={s.left}>
                {!verified && (
                    <>
                        <div className={s.name}>
                            {/* Provider Import 显示 Anonymous，不再展示 source 文案 */}
                            {review.isAnonymous || review.source === EReviewSource.PROVIDER_IMPORT
                                ? 'Anonymous'
                                : 'Verified patient'}
                            {remainingTimeInfo && (
                                <span className={`${s.timePill} ${remainingTimeInfo.className}`}>
                                    <span className={s.clockIcon}>
                                        <ClockIcon />
                                    </span>
                                    {remainingTimeInfo.text}
                                </span>
                            )}
                        </div>
                        {/* <div className={s.contacts}>
                            {hasReviewEmail && (
                                <div className={s.infoWrap}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            d="M2 5.33398L7.2604 8.84092C7.70827 9.1395 8.29173 9.1395 8.7396 8.84092L14 5.33398M3.33333 12.6673H12.6667C13.403 12.6673 14 12.0704 14 11.334V4.66732C14 3.93094 13.403 3.33398 12.6667 3.33398H3.33333C2.59695 3.33398 2 3.93094 2 4.66732V11.334C2 12.0704 2.59695 12.6673 3.33333 12.6673Z"
                                            stroke="#9CA3AF"
                                            strokeWidth="1.33333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <span className={s.info}>
                                        {review?.reviewerEmail}
                                    </span>
                                </div>
                            )}
                            {hasReviewPhone && (
                                <div className={s.infoWrap}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            d="M6.15195 2.45585L5.51949 2.66667V2.66667L6.15195 2.45585ZM7.15049 5.45147L7.78295 5.24066L7.78295 5.24066L7.15049 5.45147ZM6.81618 6.25858L6.51804 5.66229L6.81618 6.25858ZM5.31134 7.011L5.0132 6.41471C4.69317 6.57473 4.55652 6.95883 4.70359 7.28501L5.31134 7.011ZM8.989 10.6887L8.71499 11.2964C9.04117 11.4435 9.42527 11.3068 9.58529 10.9868L8.989 10.6887ZM9.74142 9.18382L9.14514 8.88568L9.14514 8.88568L9.74142 9.18382ZM10.5485 8.84951L10.7593 8.21705L10.5485 8.84951ZM13.5442 9.84805L13.3333 10.4805L13.5442 9.84805ZM3.33333 1.33333C2.22876 1.33333 1.33333 2.22876 1.33333 3.33333H2.66667C2.66667 2.96514 2.96514 2.66667 3.33333 2.66667V1.33333ZM5.51949 1.33333H3.33333V2.66667H5.51949V1.33333ZM6.78441 2.24503C6.60292 1.70057 6.0934 1.33333 5.51949 1.33333V2.66667V2.66667L6.78441 2.24503ZM7.78295 5.24066L6.7844 2.24503L5.51949 2.66667L6.51804 5.66229L7.78295 5.24066ZM7.11432 6.85486C7.7078 6.55812 7.99277 5.87014 7.78295 5.24066L6.51804 5.66229V5.66229L7.11432 6.85486ZM5.60948 7.60728L7.11432 6.85486L6.51804 5.66229L5.0132 6.41471L5.60948 7.60728ZM9.26302 10.0809C7.78142 9.41291 6.58709 8.21858 5.91909 6.73698L4.70359 7.28501C5.50524 9.06305 6.93695 10.4948 8.71499 11.2964L9.26302 10.0809ZM9.14514 8.88568L8.39272 10.3905L9.58529 10.9868L10.3377 9.48196L9.14514 8.88568ZM10.7593 8.21705C10.1299 8.00723 9.44188 8.2922 9.14514 8.88568L10.3377 9.48197V9.48196L10.7593 8.21705ZM13.755 9.2156L10.7593 8.21705L10.3377 9.48196L13.3333 10.4805L13.755 9.2156ZM14.6667 10.4805C14.6667 9.9066 14.2994 9.39708 13.755 9.2156L13.3333 10.4805H13.3333H14.6667ZM14.6667 12.6667V10.4805H13.3333V12.6667H14.6667ZM12.6667 14.6667C13.7712 14.6667 14.6667 13.7712 14.6667 12.6667H13.3333C13.3333 13.0349 13.0349 13.3333 12.6667 13.3333V14.6667ZM12 14.6667H12.6667V13.3333H12V14.6667ZM1.33333 4C1.33333 9.89104 6.10896 14.6667 12 14.6667V13.3333C6.84534 13.3333 2.66667 9.15466 2.66667 4H1.33333ZM1.33333 3.33333V4H2.66667V3.33333H1.33333Z"
                                            fill="#9CA3AF"
                                        />
                                    </svg>
                                    <span className={s.info}>
                                        {review?.reviewerPhone}
                                    </span>
                                </div>
                            )}
                        </div> */}
                    </>
                )}
                {verified && (
                    <>
                        <div className={s.name}>
                            {/* isAnonymous 时不展示 reviewerName，只展示通用文案 */}
                            {review.isAnonymous ? 'Verified patient' : review.reviewerName || 'Verified patient'}
                            {remainingTimeInfo && (
                                <span className={`${s.timePill} ${remainingTimeInfo.className}`}>
                                    <span className={s.clockIcon}>
                                        <ClockIcon />
                                    </span>
                                    {remainingTimeInfo.text}
                                </span>
                            )}
                        </div>
                        {/* <div className={s.contacts}>
                            {hasReviewEmail && (
                                <div className={s.infoWrap}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            d="M2 5.33398L7.2604 8.84092C7.70827 9.1395 8.29173 9.1395 8.7396 8.84092L14 5.33398M3.33333 12.6673H12.6667C13.403 12.6673 14 12.0704 14 11.334V4.66732C14 3.93094 13.403 3.33398 12.6667 3.33398H3.33333C2.59695 3.33398 2 3.93094 2 4.66732V11.334C2 12.0704 2.59695 12.6673 3.33333 12.6673Z"
                                            stroke="#9CA3AF"
                                            strokeWidth="1.33333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                    <span className={s.info}>
                                        {review?.reviewerEmail}
                                    </span>
                                </div>
                            )}
                            {hasReviewPhone && (
                                <div className={s.infoWrap}>
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                    >
                                        <path
                                            d="M6.15195 2.45585L5.51949 2.66667V2.66667L6.15195 2.45585ZM7.15049 5.45147L7.78295 5.24066L7.78295 5.24066L7.15049 5.45147ZM6.81618 6.25858L6.51804 5.66229L6.81618 6.25858ZM5.31134 7.011L5.0132 6.41471C4.69317 6.57473 4.55652 6.95883 4.70359 7.28501L5.31134 7.011ZM8.989 10.6887L8.71499 11.2964C9.04117 11.4435 9.42527 11.3068 9.58529 10.9868L8.989 10.6887ZM9.74142 9.18382L9.14514 8.88568L9.14514 8.88568L9.74142 9.18382ZM10.5485 8.84951L10.7593 8.21705L10.5485 8.84951ZM13.5442 9.84805L13.3333 10.4805L13.5442 9.84805ZM3.33333 1.33333C2.22876 1.33333 1.33333 2.22876 1.33333 3.33333H2.66667C2.66667 2.96514 2.96514 2.66667 3.33333 2.66667V1.33333ZM5.51949 1.33333H3.33333V2.66667H5.51949V1.33333ZM6.78441 2.24503C6.60292 1.70057 6.0934 1.33333 5.51949 1.33333V2.66667V2.66667L6.78441 2.24503ZM7.78295 5.24066L6.7844 2.24503L5.51949 2.66667L6.51804 5.66229L7.78295 5.24066ZM7.11432 6.85486C7.7078 6.55812 7.99277 5.87014 7.78295 5.24066L6.51804 5.66229V5.66229L7.11432 6.85486ZM5.60948 7.60728L7.11432 6.85486L6.51804 5.66229L5.0132 6.41471L5.60948 7.60728ZM9.26302 10.0809C7.78142 9.41291 6.58709 8.21858 5.91909 6.73698L4.70359 7.28501C5.50524 9.06305 6.93695 10.4948 8.71499 11.2964L9.26302 10.0809ZM9.14514 8.88568L8.39272 10.3905L9.58529 10.9868L10.3377 9.48196L9.14514 8.88568ZM10.7593 8.21705C10.1299 8.00723 9.44188 8.2922 9.14514 8.88568L10.3377 9.48197V9.48196L10.7593 8.21705ZM13.755 9.2156L10.7593 8.21705L10.3377 9.48196L13.3333 10.4805L13.755 9.2156ZM14.6667 10.4805C14.6667 9.9066 14.2994 9.39708 13.755 9.2156L13.3333 10.4805H13.3333H14.6667ZM14.6667 12.6667V10.4805H13.3333V12.6667H14.6667ZM12.6667 14.6667C13.7712 14.6667 14.6667 13.7712 14.6667 12.6667H13.3333C13.3333 13.0349 13.0349 13.3333 12.6667 13.3333V14.6667ZM12 14.6667H12.6667V13.3333H12V14.6667ZM1.33333 4C1.33333 9.89104 6.10896 14.6667 12 14.6667V13.3333C6.84534 13.3333 2.66667 9.15466 2.66667 4H1.33333ZM1.33333 3.33333V4H2.66667V3.33333H1.33333Z"
                                            fill="#9CA3AF"
                                        />
                                    </svg>
                                    <span className={s.info}>
                                        {review?.reviewerPhone}
                                    </span>
                                </div>
                            )}
                        </div> */}
                    </>
                )}
                {showRate && (
                    <div className={s.scoreInfo}>
                        <StarReview width={120} editable={false} value={review.rating} />
                        <div className={s.time}>{getTimeStr(dayjs(review?.submitAt), 'MMMM D, YYYY')}</div>
                        {!review.isPublic && <span className={s.private}>Private</span>}
                        {review.isPublic && <span className={s.public}>Public</span>}
                    </div>
                )}
                {hasTag && (
                    <div className={s.tagList}>
                        {review?.tags?.split(',')?.map((e, index) => {
                            return (
                                <div key={index} className={s.tag}>
                                    {e}
                                </div>
                            );
                        })}
                    </div>
                )}
                <div className={s.content}>
                    <AjustText
                        preLine
                        content={review?.comment?.content}
                        line={2}
                        isShow={isShow}
                        moreClassName={useBlueActions ? s.moreBlue : ''}
                    />
                </div>
                {hasReply && (
                    <div className={s.replyWrap}>
                        <div className={s.header}>
                            <div className={s.label}>Response from you</div>
                            <div className={s.time}>
                                {review?.repliedAt ? getTimeStr(dayjs(review?.repliedAt), 'MMMM D, YYYY') : ''}
                            </div>
                            {!showReply && (
                                <div
                                    onClick={() => {
                                        setShowReply(true);
                                    }}
                                    className={useBlueActions ? s.showTextBlue : s.showText}
                                >
                                    View response
                                </div>
                            )}
                        </div>
                        {showReply && (
                            <Spin size="small" spinning={loadingReply}>
                                <div
                                    className={s.replyContent}
                                    dangerouslySetInnerHTML={{ __html: displayComment?.content || '' }}
                                />
                            </Spin>
                        )}
                    </div>
                )}
            </div>
            {!hideRightPart && (
                <div className={s.right}>
                    {/* 已存在争议信息 */}
                    {review?.disputeStatus !== EDISPUTE_STATUS.NORMAL && (
                        <div className={s.disputeInfo}>
                            <div className={s.top}>
                                <div className={s.time}>
                                    {review?.disputeDecisionAt
                                        ? `Last updated: ${OriginDayjs(review?.disputeDecisionAt).format(
                                              'MMMM DD, YYYY',
                                          )}`
                                        : `Disputed on: ${OriginDayjs(review?.disputeSubmitAt).format(
                                              'MMMM DD, YYYY',
                                          )}`}{' '}
                                </div>
                            </div>
                            <DisputeStatus status={review?.disputeStatus} />
                            <div className={s.viewDetail} onClick={handleShowDisputeDetail}>
                                View details
                            </div>
                        </div>
                    )}

                    {/* 三个竖排点，位于 Reply 左侧，点击打开 Dispute 菜单 */}
                    {review?.disputeStatus === EDISPUTE_STATUS.NORMAL && (
                        <Dropdown
                            menu={{
                                items: menuItems,
                                onClick: handleMenuClick,
                            }}
                            trigger={['click']}
                            placement="bottomRight"
                        >
                            <div className={s.moreIconBtn}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                >
                                    <path
                                        d="M10 6C8.89543 6 8 5.10457 8 4C8 2.89543 8.89543 2 10 2C11.1046 2 12 2.89543 12 4C12 5.10457 11.1046 6 10 6Z"
                                        fill="#9CA3AF"
                                    />
                                    <path
                                        d="M10 12C8.89543 12 8 11.1046 8 10C8 8.89543 8.89543 8 10 8C11.1046 8 12 8.89543 12 10C12 11.1046 11.1046 12 10 12Z"
                                        fill="#9CA3AF"
                                    />
                                    <path
                                        d="M10 18C8.89543 18 8 17.1046 8 16C8 14.8954 8.89543 14 10 14C11.1046 14 12 14.8954 12 16C12 17.1046 11.1046 18 10 18Z"
                                        fill="#9CA3AF"
                                    />
                                </svg>
                            </div>
                        </Dropdown>
                    )}

                    {buttonRender()}
                </div>
            )}
        </div>
    );
};

export default ReviewItem;
